import React, { useEffect, useRef, useState } from 'react';
import { canonicalSlug, MODEL_DETAILS } from './catalog';
import { BACK_TEXTURES } from './materials';
import { masterSource, renderBookmark } from './rasterRenderer';
import './BookmarkArt.css';
import SylvettePhotoArt from './SylvettePhotoArt';
import { SYLVETTE_DEFAULTS } from './sylvettePhotos';
import { exactModelPhoto, photographicOnly } from './modelPhotoLibrary';
import { leonLayerScene } from './leonLayers';
import { pralinLayerScene } from './pralinLayers';
import PhotographicLayers from './PhotographicLayers';
import {remainingModelScene} from './remainingModelLayers';

export default function BookmarkArt(props) {
  if(['pepin-le-sorbet','camille-la-coquille'].includes(canonicalSlug(props.slug))){
    const scene=remainingModelScene(props);
    if(scene)return <PhotographicLayers {...props} scene={scene}
      label={props.label||`${MODEL_DETAILS[canonicalSlug(props.slug)].name}, ${props.view==='back'?'dos':'devant'}`} />;
    return <div className={`bookmark-art bookmark-art-unavailable ${props.className||''}`}
      role={props.decorative?undefined:'status'} aria-hidden={props.decorative||undefined}
      data-render-mode="photo-unavailable">
      Aperçu indisponible pour cette combinaison. Vos choix sont conservés.
    </div>;
  }
  if (canonicalSlug(props.slug) === 'pralin-le-cornet') {
    const scene = pralinLayerScene(props);
    if (scene) return <PhotographicLayers {...props} scene={scene}
      label={props.label || `Pralin le Cornet, ${props.view === 'back' ? 'dos' : 'devant'}`} />;
    return <div className={`bookmark-art bookmark-art-unavailable ${props.className || ''}`}
      role={props.decorative ? undefined : 'status'} aria-hidden={props.decorative || undefined}
      data-render-mode="photo-unavailable">
      Aperçu indisponible pour cette combinaison. Vos choix sont conservés.
    </div>;
  }
  if (canonicalSlug(props.slug) === 'sylvette-la-crevette') {
    return <SylvettePhotoArt
      front={{option_id: SYLVETTE_DEFAULTS.front_fabric}}
      back={{option_id: SYLVETTE_DEFAULTS.back_fabric}}
      ribbonFabric={{option_id: SYLVETTE_DEFAULTS.ribbon_fabric}}
      {...props} />;
  }
  if (canonicalSlug(props.slug) === 'leon-le-citron' && props.ribbonFabric?.option_id) {
    const scene = leonLayerScene(props);
    if (scene) return <PhotographicLayers {...props} scene={scene}
      label={props.label || `Léon le Citron, ${props.view === 'back' ? 'dos' : 'devant'}, ruban ${props.ribbonFabric.name || props.ribbonFabric.value || props.ribbonFabric.option_id}`} />;
    return <div className={`bookmark-art bookmark-art-unavailable ${props.className || ''}`}
      role={props.decorative ? undefined : 'status'} aria-hidden={props.decorative || undefined}
      data-render-mode="photo-unavailable">
      Aperçu indisponible pour cette combinaison. Vos choix sont conservés.
    </div>;
  }
  const exactPhoto = exactModelPhoto(props);
  if (exactPhoto) {
    return <ExactPhotoBookmarkArt {...props} photo={exactPhoto} />;
  }
  if (photographicOnly(props.slug)) {
    return <div className={`bookmark-art bookmark-art-unavailable ${props.className || ''}`}
      role={props.decorative ? undefined : 'status'}
      aria-hidden={props.decorative || undefined}
      data-render-mode="photo-unavailable">
      Aperçu indisponible pour ce tissu
    </div>;
  }
  return <RasterBookmarkArt {...props} />;
}

function ExactPhotoBookmarkArt({
  slug: rawSlug,
  view = 'front',
  className = '',
  label,
  decorative = false,
  photo,
}) {
  const slug = canonicalSlug(rawSlug);
  if (!MODEL_DETAILS[slug]) return null;
  const description =
    label ||
    `${MODEL_DETAILS[slug].name}, ${view === 'back' ? 'dos' : 'devant'}`;
  return (
    <div
      className={`bookmark-art ${className}`}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : description}
      aria-hidden={decorative || undefined}
      data-render-mode="exact-photo"
    >
      <img src={photo.src} alt="" draggable="false" />
    </div>
  );
}

// All facial detail is photographic. Material choices switch bitmap masks;
// no eyes, mouth, glasses, or decorative stitches are drawn in vector form.
function RasterBookmarkArt({
  slug: rawSlug,
  front,
  back,
  view = 'front',
  className = '',
  label,
  decorative = false,
  thumbnail = false,
}) {
  const slug = canonicalSlug(rawSlug);
  const canvas = useRef(null);
  const [state, setState] = useState('loading');
  const [renderedKey, setRenderedKey] = useState('');
  const renderKey = `${slug}:${view}`;
  const texture =
    BACK_TEXTURES[back?.option_id] ||
    back?.image ||
    BACK_TEXTURES['blue-flowers'];
  const customized = Boolean(front?.hex) || view === 'back';
  const color = front?.hex;
  useEffect(() => {
    if (!customized || !MODEL_DETAILS[slug]) {
      setRenderedKey('');
      return undefined;
    }
    let live = true;
    setState('loading');
    renderBookmark({ slug, front: { hex: color }, texture, view })
      .then((rendered) => {
        if (!live || !canvas.current) return;
        const context = canvas.current.getContext('2d');
        context.clearRect(0, 0, 1024, 1024);
        context.drawImage(rendered, 0, 0);
        setRenderedKey(`${slug}:${view}`);
        setState('ready');
      })
      .catch(() => live && setState('error'));
    return () => {
      live = false;
    };
  }, [slug, color, texture, view, customized]);
  if (!MODEL_DETAILS[slug]) return null;
  const showCanvas =
    customized && renderedKey === renderKey && state !== 'error';
  const description =
    label ||
    `${MODEL_DETAILS[slug].name}, aperçu ${view === 'back' ? 'du dos en coton fleuri' : 'du devant, visage brodé d’origine'}`;
  return (
    <div
      className={`bookmark-art ${className}`}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : description}
      aria-hidden={decorative || undefined}
      aria-busy={(customized && state === 'loading') || undefined}
      data-render-mode="legacy-raster"
    >
      {!showCanvas && (
        <img
          src={
            thumbnail
              ? masterSource(slug).replace('.webp', '-thumb.webp')
              : masterSource(slug)
          }
          alt=""
          draggable="false"
        />
      )}
      {customized && (
        <canvas
          ref={canvas}
          width="1024"
          height="1024"
          className={showCanvas ? '' : 'is-pending'}
          aria-hidden="true"
        />
      )}
      {customized && state === 'error' && (
        <span className="bookmark-art-error">
          Aperçu indisponible · modèle de référence affiché
        </span>
      )}
    </div>
  );
}
